import React, { useEffect, useMemo, useState } from "react";
import "../../client/Client.css"; // reuse styles
import OnboardingFilter from "../OnboardingFilter";
import AddOnboarding from "../Onboarding/AddOnboarding";
import config from "../../../config";
import { FiSearch } from "react-icons/fi";
import Header from "../../../components/common/Header";
import AddEmployee from "../../employee/AddEmployee";
import { BsSliders } from "react-icons/bs";


// Pretty label for UI

const statusLabel = (enumVal) =>
  String(enumVal)
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");


// Format date as YYYY-MM-DD (keeps ISO substrings as-is)
// If you prefer DD-MM-YYYY, change here.
const toYMD = (d) => {
  if (!d) return "";
  try {
    // allow already-formatted strings like "2025-11-17"
    if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}/.test(d))
      return d.slice(0, 10);

    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return String(d);
  }
};

const Onboarding = ({ setActivePage }) => {

  const [filters, setFilters] = useState({
    gender: "",
    jobRole: "",
    jobStatus: "",
    sortOrder: "",
  });

  const handleApplyFilter = (values) => {
    setFilters(values);
    fetchCandidates(values);
  };

  const handleClearFilter = () => {
    const empty = {
      gender: "",
      jobRole: "",
      jobStatus: "",
      sortOrder: "",
    };

    setFilters(empty);
    fetchCandidates(empty);
  };  // user context
  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    token: "",
    orgId: "",
  });


  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const first = localStorage.getItem("firstName") || "";
    const last = localStorage.getItem("lastName") || "";
    const role = (localStorage.getItem("role") || "GUEST").toUpperCase();
    const orgId = localStorage.getItem("orgId") || "";
    setUserDetails({ name: `${first} ${last}`.trim(), role, token, orgId });
  }, []);

  // rows from API
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterVisible, setFilterVisible] = useState(false);
  const itemsPerPage = 8;
  const [totalElements, setTotalElements] = useState(0);

  // ---- Filtering (search) ----
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.candidateName.toLowerCase().includes(q) ||
        r.client.toLowerCase().includes(q) ||
        r.job.toLowerCase().includes(q) ||
        statusLabel(r.status).toLowerCase().includes(q) ||
        (r.doj || "").toLowerCase().includes(q)
    );
  }, [rows, searchTerm]);

  const [showAddOnboarding, setShowAddOnboarding] = useState(false);
  const [selectedOnboarding, setSelectedOnboarding] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [showFilter, setShowFilter] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalElements / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const pageRows = filtered;

  // --- data/ui state ---

  const [error, setError] = useState(null);

  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const [candidates, setCandidates] = useState([]);

  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [convertData, setConvertData] = useState(null);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  // ---- Fetch candidates from API (read-only rendering) ----
  // fetch list of candidates (can be called to refresh)
  const fetchCandidates = async (filterValues = filters) => {
    if (!userDetails.token) return;

    setLoading(true);

    try {
      const orgKey = userDetails.orgId || 6;

      const url =
        `${config.API_BASE_URL}${config.ONBOARDING_LIST_ENDPOINT}` +
        `?search=${searchTerm}` +
        `&jobRole=${filterValues.jobRole}` +
        `&jobStatus=${filterValues.jobStatus}` +
        `&gender=${filterValues.gender}` +
        `&sortOrder=${filterValues.sortOrder}` +
        `&sortBy=createdAt` +
        `&page=${currentPage - 1}` +
        `&size=${itemsPerPage}` +
        `&orgKey=${orgKey}`;

      console.log("Filter API:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      const list = Array.isArray(data?.content) ? data.content : [];

      const normalized = list.map((c, idx) => ({
        id: c.id ?? idx + 1,
        candidateName: c.candidateName || "",
        client: c.client || "",
        job: c.jobRole || "",
        status: c.jobStatus,
        doj: toYMD(c.dateOfJoining),
      }));

      setRows(normalized);
      setTotalElements(data?.totalElements || normalized.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, [userDetails.token, currentPage, searchTerm]);

  const handleRowClick = async (id) => {
    const res = await fetch(
      `${config.API_BASE_URL}${config.ONBOARDING_VIEW_LIST_ENDPOINT}/${id}`,
      {
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );

    const c = await res.json();

    // ✅ NORMALIZE onboarding → AddOnboarding format
    const normalizedCandidate = {
      id: c.id,

      // candidate info
      name: c.candidateName,
      aadhar: c.aadharNumber,
      uan: c.uanNumber,
      email: c.email,
      phone: c.phoneNumber,
      dob: c.dateOfBirth,
      gender: c.gender,

      // job info
      clientName: c.client,
      subVendorName: c.subVendor,
      jobRole: c.jobRole,
      qualification: c.qualification,
      skillsConfigured: c.skills,

      interviewDate: c.interviewDate,
      interviewTime: c.interviewTime,

      // address
      addressLine1: c.address,
      city: c.city,
      district: c.district,
      state: c.state,
      country: c.country,
      pinCode: c.pincode,

      // onboarding
      pocName: c.pointOfContact,
      pocRole: c.pocRole,
      jobStatus: c.jobStatus,
      dateOfJoining: c.dateOfJoining,
    };

    setSelectedOnboarding(normalizedCandidate);
    setShowAddOnboarding(true);
  };

  if (showAddOnboarding && selectedOnboarding) {
    return (
      <AddOnboarding
        selectedCandidate={selectedOnboarding}
        fromConvert={false}
        onCancel={() => {
          setShowAddOnboarding(false);
          setSelectedOnboarding(null);
        }}
        onConvertToEmployee={(data) => {
          console.log("Converting to employee:", data);

          setConvertData(data);
          setShowAddOnboarding(false); // close onboarding
          setShowAddEmployee(true); // open employee
        }}
      />
    );
  }

  if (showAddEmployee) {
    return (
      <AddEmployee
        convertData={convertData}
        onCancel={() => {
          setShowAddEmployee(false);
          setConvertData(null);
        }}
      />
    );
  }

  // --- Render ---
  return (
    <div>
      {/* Header */}
      <Header
        title="Onboarding List"
        subtitle="Onboarding Information"
        onProfileClick={() => setActivePage && setActivePage("Profile")}
      />

      {/* Table card */}
      <div className='table-container'>
        {errorMsg && (
          <div style={{ color: "red", paddingBottom: "8px" }}>{errorMsg}</div>
        )}

        {/* Controls */}
        <div className='table-controls'>
          <div
            className="search-wrapper"
            style={{ position: "relative", display: "flex", alignItems: "center" }}
          >
            <FiSearch
              style={{
                position: "absolute",
                left: "15px",
                fontSize: "14px",
                color: "#999",
              }}
            />

            <input
              type="text"
              className="search-input"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{
                paddingLeft: "38px",
                width: "150px",
                height: "15px",
              }}
            />
          </div>
          <div className='button-container'>
            <button className='filter-btn' onClick={() => setShowFilter(true)}>
              <BsSliders /> Filter
            </button>
          </div>
        </div>

        <OnboardingFilter
          open={showFilter}
          onClose={() => setShowFilter(false)}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
          initialValues={filters}
        />

        <table className='table'>
          <thead>
            <tr>
              <th className='th'>Candidate Name</th>
              <th className='th'>Client</th>
              <th className='th'>Job Position</th>
              <th className='th'>Job Status</th>
              <th className='th'>Date of Joining</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className='td' colSpan={5} style={{ textAlign: "center", padding: "20px" }}>
                  Loading onboarding records...
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td className='td' colSpan={5} style={{ textAlign: "center" }}>
                  No onboarding records found
                </td>
              </tr>
            ) : (
              pageRows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => handleRowClick(r.id)}
                  style={{ cursor: "pointer" }}
                >
                  <td className='td'>{r.candidateName}</td>
                  <td className='td'>{r.client}</td>
                  <td className='td'>{r.job}</td>
                  {/* READ-ONLY labels for status & DOJ */}
                  <td className='td'>{statusLabel(r.status)}</td>
                  <td className='td'>{r.doj || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Footer / Pagination */}
        <div className='table-footer'>
          <div className='record-count'>
            Showing {totalElements === 0 ? 0 : indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, totalElements)} of {totalElements}{" "}
            entries
          </div>

          <div className='pagination'>
            <button
              className='pagination-button'
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              &lt;
            </button>

            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`pagination-button ${currentPage === i + 1 ? "active" : ""
                  }`}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </button>
            ))}

            <button
              className='pagination-button'
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
